/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import {
	useIsChrome128OrOlder,
	useIsUnrealEngineContext,
	useIsMobile,
	isChrome128OrOlder,
	isUnrealEngineContext,
} from "./userAgent.hooks";

const MAX_BLUR_REDUCED = 12;

/**
 * Detects if backdrop-filter blur should be reduced to 12px max for performance
 * Returns true for Chrome 128, UnrealEngine, and mobile/tablet devices
 */
export const useShouldReduceBlur = (): boolean => {
	const isOldChrome = useIsChrome128OrOlder();
	const isUE = useIsUnrealEngineContext();
	const isMobileDevice = useIsMobile();

	return isOldChrome || isUE || isMobileDevice;
};

/**
 * Non-hook version of useShouldReduceBlur for use outside React components
 * Note: Cannot detect mobile in SSR context
 */
export const shouldReduceBlur = (): boolean => {
	return isChrome128OrOlder() || isUnrealEngineContext();
};

/**
 * Caps blur value to 12px max when shouldReduce is true
 * @param blurValue - Blur value (e.g., "50px" or "blur(100px)")
 * @param shouldReduce - Whether to apply reduction
 */
export const getOptimalBlur = (blurValue: string, shouldReduce: boolean): string => {
	if (!shouldReduce) {
		return blurValue;
	}

	const numericMatch = blurValue.match(/(\d+)px/);
	if (!numericMatch) {
		return blurValue;
	}

	const currentBlur = parseInt(numericMatch[1], 10);

	if (currentBlur <= MAX_BLUR_REDUCED) {
		return blurValue;
	}

	if (blurValue.startsWith("blur(")) {
		return `blur(${MAX_BLUR_REDUCED}px)`;
	}
	return `${MAX_BLUR_REDUCED}px`;
};

/**
 * Generates backdrop-filter CSS with device-optimized blur value
 * @param desiredBlur - Desired blur for modern browsers (e.g., "50px")
 * @param shouldReduce - Whether to reduce blur for performance
 */
export const getBackdropFilterBlur = (desiredBlur: string, shouldReduce: boolean): string => {
	const blurValue = desiredBlur.includes("blur(") ? desiredBlur : `blur(${desiredBlur})`;
	return getOptimalBlur(blurValue, shouldReduce);
};
